import { useState, useEffect } from 'react'
import { subscribeKey } from 'valtio/utils'
import styles from './InfoViewPanel.module.css'
import type { ReplayEvent, ReplayEventType } from '../../../packetsReplay/extractReplayEvents'
import PlayerAvatar from './PlayerAvatar'
import CollapseTab from './CollapseTab'
import { packetsReplayState } from '../../state/packetsReplayState'
import { EVENT_ICON, EVENT_NAME } from './eventVisuals'

interface Props {
  replayName: string
  isPlaying: boolean
  progress: { current: number; total: number }
  totalTimeMs?: number
  currentTimeMs?: number
  packetTimesMs?: readonly number[]
  replayEvents?: ReplayEvent[]
  onSeekToTime?: (ms: number, packetIndex?: number) => void
}

const fmt = (ms: number) => {
  const s = Math.floor(ms / 1000)
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

function extractMcText (obj: unknown): string {
  if (typeof obj === 'string') return obj
  if (!obj || typeof obj !== 'object') return ''
  const o = obj as Record<string, unknown>
  let out = typeof o.text === 'string' ? o.text : ''
  if (o.translate && Array.isArray(o.with)) {
    out = (o.with as unknown[]).map(extractMcText).join(' ')
  }
  if (Array.isArray(o.extra)) {
    out += (o.extra as unknown[]).map(extractMcText).join('')
  }
  return out.trim()
}

function cleanLabel (label: string | undefined): string {
  if (!label) return ''
  const s = label.trim()
  if (!s.startsWith('{') && !s.startsWith('[')) return s
  try {
    return extractMcText(JSON.parse(s))
  } catch {
    return ''
  }
}

/* Kind → color class. Matches the timeline palette exactly: fight red,
   chat blue, command GREEN (was wrongly blue here), movement/activity GOLD
   (was wrongly orange) — one event kind, one color, everywhere. */
const EVENT_COLOR_CLASS: Partial<Record<ReplayEventType, string>> = {
  death:            'death',
  damage:           'damage',
  kill_player:      'death',
  kill_mob:         'damage',
  chat:             'chat',
  command:          'command',
  dimension_change: 'dim',
  respawn:          'dim',
  teleport:         'dim',
  afk:              'afk',
  afk_end:          'afk',
  high_activity:    'dim',
}

function labelOfEvent (ev: ReplayEvent): string {
  const cleaned = cleanLabel(ev.label)
  if (cleaned) return cleaned
  return EVENT_NAME[ev.type] ?? ev.type
}

function formatRelative (ms: number): string {
  const abs = Math.abs(ms)
  if (abs < 10_000) return `${(abs / 1000).toFixed(1)}с`
  if (abs < 60_000) return `${Math.round(abs / 1000)}с`
  const m = Math.floor(abs / 60_000)
  const s = Math.round((abs % 60_000) / 1000)
  return `${m}м ${s.toString().padStart(2, '0')}с`
}


const NOW_WINDOW_MS = 1500   // event is highlighted as "идёт сейчас" within this many ms after fire
const RECENT_WINDOW_MS = 6000 // longer tail for "только что"

export default function InfoViewPanel ({ replayName, isPlaying, progress, totalTimeMs, currentTimeMs, packetTimesMs, replayEvents, onSeekToTime }: Props) {
  const [hidden, setHidden] = useState(packetsReplayState.infoViewCollapsed)
  // Let external toggles (TopMenu "hide UI", via packetsReplayState.infoViewCollapsed)
  // drive collapse — toggleHidden still writes the same flag, so no feedback loop.
  useEffect(() => subscribeKey(packetsReplayState, 'infoViewCollapsed', (v) => setHidden(v)), [])
  const toggleHidden = (v: boolean) => {
    setHidden(v)
    packetsReplayState.infoViewCollapsed = v
  }

  const playerName = (globalThis as any).bot?.username ?? null
  const hasTime    = !!(totalTimeMs && totalTimeMs > 0)
  const evts       = replayEvents ?? []
  const pointEvts  = evts.filter(e => e.type !== 'low_activity')

  const getTimeForIndex = (index: number) => {
    if (!hasTime || progress.total <= 0) return 0
    const fallback = (index / progress.total) * totalTimeMs!
    if (!packetTimesMs?.length) return fallback
    const clamped = Math.max(0, Math.min(packetTimesMs.length - 1, Math.round(index)))
    return packetTimesMs[clamped] ?? fallback
  }

  const currentMs = typeof currentTimeMs === 'number' ? currentTimeMs : getTimeForIndex(progress.current)

  // Last point event whose time ≤ currentMs
  let passedIdx = -1
  for (let i = 0; i < pointEvts.length; i++) {
    if (pointEvts[i]!.time <= currentMs + 50) passedIdx = i
    else break
  }
  const lastPassed = passedIdx >= 0 ? pointEvts[passedIdx] : undefined
  const nextEvent  = passedIdx + 1 < pointEvts.length ? pointEvts[passedIdx + 1] : undefined

  // Are we currently inside a low_activity segment?
  const insideLow = evts.find(ev =>
    ev.type === 'low_activity'
    && typeof ev.duration === 'number'
    && ev.time <= currentMs
    && currentMs <= ev.time + ev.duration
  )

  // Choose the active event to display
  const sinceLast = lastPassed ? currentMs - lastPassed.time : Infinity
  const isHappeningNow = sinceLast <= NOW_WINDOW_MS
  const isRecent       = sinceLast <= RECENT_WINDOW_MS

  let activeEvent: ReplayEvent | undefined
  if (isHappeningNow || isRecent) {
    activeEvent = lastPassed
  } else if (insideLow) {
    activeEvent = insideLow
  }

  const title = activeEvent
    ? labelOfEvent(activeEvent)
    : (replayName || 'Запись')
  const colorClass = activeEvent ? (EVENT_COLOR_CLASS[activeEvent.type] ?? '') : ''

  let subtitle = ''
  if (activeEvent === lastPassed && lastPassed) {
    subtitle = isHappeningNow ? '→ идёт сейчас' : `${formatRelative(sinceLast)} назад`
  } else if (activeEvent && activeEvent === insideLow && insideLow.duration) {
    const remaining = (insideLow.time + insideLow.duration) - currentMs
    subtitle = remaining > 0 ? `ещё ${formatRelative(remaining)}` : ''
  } else if (!activeEvent && nextEvent) {
    subtitle = `ждём — ${formatRelative(nextEvent.time - currentMs)}`
  } else if (!activeEvent && hasTime) {
    subtitle = 'начало записи'
  }

  // Bottom progress info: how many events passed / total
  const passedCount = passedIdx + 1
  const totalCount  = pointEvts.length

  // Hint about the next event in the queue (if any)
  const nextHint = nextEvent
    ? `→ ${EVENT_NAME[nextEvent.type] ?? nextEvent.type} через ${formatRelative(nextEvent.time - currentMs)}`
    : null

  const goToEvent = (dir: 1 | -1) => {
    const evts = replayEvents
    if (!evts?.length || !onSeekToTime) return
    // Reuse the outer currentMs: it honours the precise currentTimeMs the
    // engine reports; re-deriving from the packet index drifted after
    // time-based seeks and made prev/next land on the wrong neighbour.
    const MARGIN = 500
    let targetIdx: number
    if (dir > 0) {
      targetIdx = evts.findIndex(e => e.time > currentMs + MARGIN)
      if (targetIdx === -1) targetIdx = evts.length - 1
    } else {
      targetIdx = -1
      for (let i = evts.length - 1; i >= 0; i--) {
        if (evts[i]!.time < currentMs - MARGIN) { targetIdx = i; break }
      }
      if (targetIdx === -1) targetIdx = 0
    }
    onSeekToTime(evts[targetIdx]!.time, evts[targetIdx]!.packetIndex)
  }

  return (
    <div
      className={[styles.panelWrap, hidden && styles.panelWrapCollapsed].filter(Boolean).join(' ')}
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      data-replay-ui
      data-holo-panel-wrap
      data-panel-collapsed={hidden}
    >
      <div className={[styles.panel, hidden && styles.panelHidden].filter(Boolean).join(' ')}>
      {/* Replay name — full-width, wrappable, small font */}
      <div className={styles.replayNameRow}>
        <span className={styles.replayName}>{replayName || 'replay'}</span>
      </div>

      {/* Time hero — the panel's most important number, so it gets DISPLAY
         size (telemetry-clock logic): giant airy current time, small total,
         deliberately tiny label. Semantics through size, not color. */}
      {hasTime && (
        <div className={styles.timeHero}>
          <span className={styles.timeHeroLabel}>время реплея</span>
          <div className={styles.timeHeroRow}>
            <span className={styles.timeHeroNow}>{fmt(currentMs)}</span>
            <span className={styles.timeHeroSep}>/</span>
            <span className={styles.timeHeroAll}>{fmt(totalTimeMs)}</span>
          </div>
        </div>
      )}

      {/* Packet counter — second-rank number: big current, small total,
         micro label. */}
      <div className={styles.counterRow}>
        <span className={styles.counterIcon}>{isPlaying ? '▶' : '⏸'}</span>
        <span className={styles.counterNum}>{progress.current}</span>
        <span className={styles.counterSep}>/</span>
        <span className={styles.counterTotal}>{progress.total}</span>
        <span className={styles.counterLabel}>пакеты</span>
      </div>

      {/* Event card — same head anatomy as the timeline popup's detail card
         (kind-colored icon square + name + pixel time), so the two surfaces
         visibly show one and the same entity. */}
      <div
        className={styles.activeBlock}
        data-state={isHappeningNow ? 'now' : (isRecent ? 'recent' : 'idle')}
      >
        {activeEvent ? (
          <div className={styles.cardHead}>
            <span className={[styles.cardIcon, colorClass ? styles[colorClass as keyof typeof styles] : ''].filter(Boolean).join(' ')}>
              {EVENT_ICON[activeEvent.type]
                ? <span className={`pixelart-icons-font-${EVENT_ICON[activeEvent.type]}`} />
                : '●'}
            </span>
            <span className={[styles.eventTitle, colorClass ? styles[colorClass as keyof typeof styles] : ''].filter(Boolean).join(' ')}>{title}</span>
            <span className={styles.cardTime}>{fmt(activeEvent.time)}</span>
          </div>
        ) : (
          <div className={styles.eventTitle}>{title}</div>
        )}
        {subtitle && <div className={styles.eventTime}>{subtitle}</div>}
        {nextHint && !isHappeningNow && <div className={styles.nextHint}>{nextHint}</div>}
      </div>

      {/* Player row */}
      {playerName && (
        <div className={styles.playerRow}>
          <span className={styles.avatar}><PlayerAvatar name={playerName} size={30} /></span>
          <span className={styles.playerName}>{playerName}</span>
        </div>
      )}

      <div className={styles.divider} />

      {/* Events nav */}
      <div className={styles.recsHeader}>
        <div>
          <div className={styles.recsLabel}>События</div>
          {hasTime && totalCount > 0 && (
            <div className={styles.recsCount}>{passedCount} / {totalCount} пройдено</div>
          )}
          {hasTime && totalCount === 0 && (
            <div className={styles.recsCount}>нет событий</div>
          )}
        </div>
        <div className={styles.navBtns}>
          <button
            className={styles.iconBtn}
            title="Предыдущее событие"
            onClick={() => goToEvent(-1)}
            disabled={!replayEvents?.length || !onSeekToTime}
          >‹</button>
          <button
            className={styles.iconBtn}
            title="Следующее событие"
            onClick={() => goToEvent(1)}
            disabled={!replayEvents?.length || !onSeekToTime}
          >›</button>
        </div>
      </div>

      </div>

      {/* Collapse / expand — shared teal-accent tab on the right edge. */}
      <CollapseTab
        edge="right"
        collapsed={hidden}
        onToggle={() => toggleHidden(!hidden)}
      />
    </div>
  )
}
